// TablePage.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { API_ENDPOINTS } from "../config/constants";
import {
  FaClock,
  FaPlus,
  FaShoppingBag,
  FaSyncAlt,
  FaTimes,
  FaTrashAlt,
} from "react-icons/fa";

/* ---------------- Firebase (web, modular v9) ---------------- */
import { initializeApp, getApps } from "firebase/app";
import { getDatabase, ref, get, onValue, set } from "firebase/database";
import { APP_ESCAPE_EVENT, APP_SHORTCUT_EVENT } from "../utils/shortcuts";
import { formatMoney, resolveCurrencyContext } from "../utils/currency";
import { resolveTaxLabelFromProfile } from "../utils/taxDetails";
import { toLocalYmd } from "../utils/localDate";

/* ---------------- Brand / theme ---------------- */
const brandColor = "#015b9a";

/* ---------------- Helpers (parity with mobile) ---------------- */
const TAKEAWAY_STORAGE_KEY = "takeawaySlots";
const DEFAULT_TAKEAWAY_NAME = "TakeAway-1";
const TABLE_CACHE_KEY = "tablelistx";
const REQUEST_DEDUPE_TTL_MS = 1500;
const TRIAL_ORDER_LIMIT = 25;
const tableApiRequestCache = new Map();

const getCachedAsync = async (
  cache,
  key,
  loader,
  { force = false, ttlMs = REQUEST_DEDUPE_TTL_MS } = {}
) => {
  const cacheKey = String(key || "");
  const now = Date.now();
  const cached = cache.get(cacheKey);

  if (!force && cached) {
    if (cached.promise) return cached.promise;
    if (cached.hasValue && now - cached.timestamp < ttlMs) {
      return cached.value;
    }
  }

  const promise = Promise.resolve()
    .then(loader)
    .then((value) => {
      cache.set(cacheKey, {
        hasValue: true,
        value,
        timestamp: Date.now(),
      });
      return value;
    })
    .catch((error) => {
      cache.delete(cacheKey);
      throw error;
    });

  cache.set(cacheKey, {
    ...(cached?.hasValue ? { hasValue: true, value: cached.value } : {}),
    promise,
    timestamp: now,
  });

  return promise;
};

const isTakeawayName = (name) =>
  typeof name === "string" && name.toLowerCase().startsWith("takeaway");

const extractTakeawayIndex = (name) => {
  if (!name) return null;
  const lower = String(name).toLowerCase().trim();
  if (lower === "takeaway") return 1;
  const m = lower.match(/takeaway[\s-]*(\d+)/);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return Number.isNaN(n) ? null : n;
};

const canonicalizeTakeawayName = (name) => {
  if (!isTakeawayName(name)) return DEFAULT_TAKEAWAY_NAME;
  const idx = extractTakeawayIndex(name);
  return `TakeAway-${idx && idx > 0 ? idx : 1}`;
};

const formatTakeawayLabel = (name) => {
  const idx = extractTakeawayIndex(name);
  return idx ? `TakeAway ${idx}` : String(name || "TakeAway");
};

const safeNumber = (v, fallback = null) => {
  if (v === 0 || v === "0") return 0;
  if (v === undefined || v === null || v === "") return fallback;
  const n = Number(v);
  return Number.isNaN(n) ? fallback : n;
};

const normalizeItemCount = (value) => {
  const count = Number(value || 0);
  return Number.isFinite(count) && count > 0 ? Math.trunc(count) : 0;
};

const sumLineItemQty = (lineItems) => {
  if (!Array.isArray(lineItems)) return 0;
  return lineItems.reduce((sum, line) => {
    const qty = Number(line?.quantity ?? line?.qty ?? 0);
    return qty > 0 ? sum + qty : sum;
  }, 0);
};

const sumProductQtyMap = (products) => {
  if (!products || typeof products !== "object") return 0;
  return Object.values(products).reduce((sum, qty) => {
    const numericQty = Number(qty || 0);
    return numericQty > 0 ? sum + numericQty : sum;
  }, 0);
};

const resolveItemCount = ({ lineItems = null, products = null, fallback = 0 } = {}) => {
  const lineQty = sumLineItemQty(lineItems);
  const productQty = sumProductQtyMap(products);
  return Math.max(lineQty, productQty, normalizeItemCount(fallback));
};

const getDurationSourceValue = (item) =>
  item?.durationSource ??
  item?.startedAt ??
  item?.orderStartTime ??
  item?.durationStartedAt ??
  item?.duration ??
  null;

const getTableKey = (table) => {
  if (!table) return "";
  const candidate =
    table?.table_key ??
    table?.tableKey ??
    table?.key ??
    table?.name ??
    table?.table ??
    table?.tableid ??
    table?.tableId ??
    table?.id ??
    "";
  return candidate === null || candidate === undefined ? "" : String(candidate);
};

const getTableDisplayName = (table) => {
  if (!table) return "";
  const candidate =
    table?.display_name ??
    table?.displayName ??
    table?.table_key ??
    table?.tableKey ??
    table?.name ??
    table?.table ??
    table?.tableid ??
    table?.tableId ??
    table?.id ??
    "";
  return candidate === null || candidate === undefined ? "" : String(candidate);
};

const getTableType = (table) => {
  const raw = table?.table_type ?? table?.tableType ?? table?.type;
  return raw === "room" ? "room" : "table";
};

const getCircleLabelFontSize = (value) => {
  const len = String(value || "").trim().length;
  if (len >= 9) return 11;
  if (len >= 6) return 12;
  return 14;
};

const formatDuration = (diffMs) => {
  const totalSec = Math.max(0, Math.floor(diffMs / 1000));
  const hours = Math.floor(totalSec / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  if (hours > 0) {
    return `${hours} hr ${mins} min ${secs < 10 ? "0" : ""}${secs} sec`;
  }
  return `${mins} min ${secs < 10 ? "0" : ""}${secs} sec`;
};

const durationFromValue = (value) => {
  if (!value) return "0";
  const numericValue = Number(value);
  if (Number.isFinite(numericValue) && numericValue > 0) {
    const epochMs =
      numericValue > 1e12
        ? numericValue
        : numericValue > 1e9
        ? numericValue * 1000
        : null;

    if (epochMs) {
      return formatDuration(Date.now() - epochMs);
    }
  }
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) {
    return formatDuration(Date.now() - d.getTime());
  }
  return String(value);
};

const hasPositiveQty = (v) => Number(v) > 0;
const buildHasProducts = (entry) => {
  if (!entry || typeof entry !== "object") return false;
  if (Array.isArray(entry?.line_items)) {
    return entry.line_items.some((line) =>
      hasPositiveQty(line?.quantity ?? line?.qty ?? 0)
    );
  }
  if (entry?.products && typeof entry.products === "object") {
    return Object.values(entry.products).some((qty) => hasPositiveQty(qty));
  }
  return !!entry?.hasdata;
};

const parseJSON = (raw, fallback = null) => {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

/* ---------------- Firebase config (same as mobile) ---------------- */
const RNfirebaseConfig = {
  apiKey: "AIzaSyDbfZS3ZazugHXoSIvBk--P6coVstYX7R8",
  projectId: "estro-mobile-app",
  storageBucket: "restro-mobile-app.firebasestorage.app",
  messagingSenderId: "7236168554",
  appId: "1:7236168554:ios:d5114808c53aadb4671313",
  databaseURL:
    "https://restro-mobile-app-default-rtdb.asia-southeast1.firebasedatabase.app",
};

function ensureFirebase() {
  if (getApps().length === 0) initializeApp(RNfirebaseConfig);
  return getDatabase();
}

/* ---------------- UI bits ---------------- */
const Chip = ({ children, tone = "default" }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "8px 12px",
      borderRadius: 999,
      fontWeight: 700,
      fontSize: 12,
      border: "1px solid",
      background:
        tone === "success"
          ? brandColor
          : tone === "muted"
          ? "#F1F5F9"
          : "#EEF2FF",
      color: tone === "success" ? "#fff" : brandColor,
      borderColor: tone === "success" ? brandColor : "#CBD5E1",
    }}
  >
    {children}
  </span>
);

const Modal = ({ open, onClose, children, title }) => {
  if (!open) return null;
  return (
    <div style={modalStyles.backdrop} onClick={onClose}>
      <div style={modalStyles.card} onClick={(e) => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <h3 style={{ margin: 0, color: "#0F172A" }}>{title}</h3>
          <button
            onClick={onClose}
            style={modalStyles.closeBtn}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>
        <div>{children}</div>
      </div>
    </div>
  );
};

const modalStyles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.45)",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-end",
    padding: 20,
    zIndex: 50,
  },
  card: {
    width: "100%",
    maxWidth: 560,
    background: "#fff",
    borderRadius: 16,
    padding: 16,
    boxShadow: "0 20px 40px rgba(0,0,0,.25)",
    maxHeight: "86vh",
    overflowY: "auto",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  closeBtn: {
    border: "1px solid #E2E8F0",
    background: "#F8FAFC",
    borderRadius: 10,
    padding: "6px 10px",
    cursor: "pointer",
  },
};

/* ---------------- Component ---------------- */
const TablePage = () => {
  const navigate = useNavigate();

  const [tableList, setTableList] = useState([]);
  const [activeCount, setActiveCount] = useState(0);
  const [inactiveCount, setInactiveCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const [tableMetaMap, setTableMetaMap] = useState({});
  const [takeawaySlots, setTakeawaySlots] = useState([DEFAULT_TAKEAWAY_NAME]);
  const [takeawayManagerOpen, setTakeawayManagerOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [planExpired, setPlanExpired] = useState(false);
  const [durationTick, setDurationTick] = useState(0);

  const [tableActionOpen, setTableActionOpen] = useState(false);
  const [tableActionLoading, setTableActionLoading] = useState(false);
  const [tableActionTarget, setTableActionTarget] = useState(null);
  const [tableActionLines, setTableActionLines] = useState([]);
  const [tableActionTotal, setTableActionTotal] = useState(0);
  const [tableActionMeta, setTableActionMeta] = useState({
    customerName: "",
    discountText: "",
    gstAmount: 0,
  });

  const pressTimeoutRef = useRef(null);
  const baseTableListRef = useRef([]);
  const takeawaySlotsRef = useRef([DEFAULT_TAKEAWAY_NAME]);
  const firebaseUnsubscribeRef = useRef(null);
  const firebaseTableStateRef = useRef(null);
  const tableMetaMapRef = useRef({});

  const userData = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userData")) || null;
    } catch {
      return null;
    }
  }, []);
  const restaurantId = userData?.data?.id;
  const fmtMoney = (value) => formatMoney(value, resolveCurrencyContext(userData));
  const taxDisplayLabel = resolveTaxLabelFromProfile(userData?.data || {});

  const loadPlanValidity = useCallback(async () => {
    const stored = parseJSON(localStorage.getItem("checkvalidData"), null);
    const validityExpired = stored?.valid === false;

    if (!validityExpired) {
      setPlanExpired(false);
      return;
    }

    const profile = userData?.data || {};
    const isTrialUser =
      Number(profile?.has_purchased ?? profile?.has_purchase ?? 0) === 0;

    // A paid plan still follows its normal validity date. For a trial, expiry is
    // enforced only after both the time window and the 25-order allowance end.
    if (!isTrialUser) {
      setPlanExpired(true);
      return;
    }

    const createdAt = new Date(
      String(profile?.created_at || profile?.createdAt || "").replace(" ", "T")
    );
    const fromDate = Number.isNaN(createdAt.getTime())
      ? toLocalYmd(new Date())
      : toLocalYmd(createdAt);

    try {
      const response = await fetch(
        API_ENDPOINTS.REPORT_SALES,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            restaurantid: restaurantId,
            fromdate: fromDate,
            todate: toLocalYmd(new Date()),
          }),
        }
      );
      const result = await response.json().catch(() => ({}));
      const orderCount = Number(result?.total_sale_count);
      setPlanExpired(
        response.ok && Number.isFinite(orderCount) && orderCount >= TRIAL_ORDER_LIMIT
      );
    } catch (_) {
      // Never block a trial user when the order allowance cannot be verified.
      setPlanExpired(false);
    }
  }, [restaurantId, userData]);

  const updateCounts = useCallback((list) => {
    const active = (list || []).filter((x) => x?.hasdata).length;
    setActiveCount(active);
    setInactiveCount((list || []).length - active);
  }, []);

  /* ---- localStorage slots ---- */
  const getStoredTakeawaySlots = useCallback(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(TAKEAWAY_STORAGE_KEY));
      if (Array.isArray(raw) && raw.length > 0) {
        const canon = Array.from(
          new Set(raw.map((s) => canonicalizeTakeawayName(s)))
        );
        return canon.length ? canon : [DEFAULT_TAKEAWAY_NAME];
      }
    } catch {}
    return [DEFAULT_TAKEAWAY_NAME];
  }, []);

  const persistTakeawaySlots = useCallback((slots) => {
    const canon = Array.from(
      new Set((slots || []).map(canonicalizeTakeawayName))
    );
    const final = canon.length ? canon : [DEFAULT_TAKEAWAY_NAME];
    localStorage.setItem(TAKEAWAY_STORAGE_KEY, JSON.stringify(final));
    takeawaySlotsRef.current = final;
    setTakeawaySlots(final);
    return final;
  }, []);

  const getCachedTableList = useCallback(() => {
    try {
      const raw = localStorage.getItem(TABLE_CACHE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, []);

  const cacheTableList = useCallback((list) => {
    try {
      localStorage.setItem(TABLE_CACHE_KEY, JSON.stringify(list || []));
    } catch {}
  }, []);

  /* ---- metadata (seatcapacity, notes) from API list ---- */
  const buildTableMetaMap = useCallback((list) => {
    const map = {};
    (list || []).forEach((item) => {
      const key = String(getTableKey(item));
      if (!key) return;
      const seatRaw =
        item?.seatcapacity ??
        item?.seatCapacity ??
        item?.seat_capacity ??
        item?.seatCapcity;
      map[key] = {
        seatcapacity: safeNumber(seatRaw, null),
        notes: item?.notes ?? null,
      };
    });
    return map;
  }, []);

  const updateTableMetaMap = useCallback((meta) => {
    const next = meta || {};
    tableMetaMapRef.current = next;
    setTableMetaMap(next);
  }, []);

  /* ---- merge: takeaway slots + others (same as mobile) ---- */
  const mergeTakeawaySlots = useCallback((list = [], slots = []) => {
    const normalizedSlots =
      Array.isArray(slots) && slots.length
        ? slots.map(canonicalizeTakeawayName)
        : [DEFAULT_TAKEAWAY_NAME];
    const slotSet = new Set(normalizedSlots);

    const takeawaysMap = new Map();
    const others = [];

    list.forEach((raw) => {
      const nm = raw?.name ?? raw?.table ?? raw?.id;
      if (isTakeawayName(nm)) {
        const c = canonicalizeTakeawayName(nm);
        slotSet.add(c);
        takeawaysMap.set(c, {
          ...raw,
          name: c,
          id: c,
          table_key: c,
          display_name: formatTakeawayLabel(c),
          label: formatTakeawayLabel(c),
        });
      } else {
        others.push(raw);
      }
    });

    const orderedSlots = Array.from(slotSet)
      .map((name) => ({
        name,
        index: extractTakeawayIndex(name) || Number.MAX_SAFE_INTEGER,
      }))
      .sort((a, b) => a.index - b.index)
      .map((e) => e.name);

    const slotEntries = orderedSlots.map(
      (slotName) =>
        takeawaysMap.get(slotName) || {
          hasdata: false,
          name: slotName,
          id: slotName,
          table_key: slotName,
          display_name: formatTakeawayLabel(slotName),
          label: formatTakeawayLabel(slotName),
          amount: 0,
          duration: "0",
        }
    );

    return { list: [...slotEntries, ...others], slots: orderedSlots };
  }, []);

  /* ---- merge metadata into list ---- */
  const mergeMetadata = useCallback(
    (list, overrideMeta) =>
      (list || []).map((it) => {
        const key = String(getTableKey(it));
        const meta = (overrideMeta || tableMetaMapRef.current || {})[key] || {};
        const seatRaw =
          meta?.seatcapacity ??
          meta?.seatCapacity ??
          it?.seatcapacity ??
          it?.seatCapacity;
        const seat = safeNumber(seatRaw, null);
        const notes =
          meta?.notes !== undefined
            ? meta.notes
            : it?.notes !== undefined
            ? it.notes
            : null;
        return { ...it, seatcapacity: seat, notes };
      }),
    []
  );

  /* ---- API: fetch tables ---- */
  const fetchTables = useCallback(async ({ force = false } = {}) => {
    if (!restaurantId) return { list: [] };
    return getCachedAsync(
      tableApiRequestCache,
      restaurantId,
      async () => {
        const res = await fetch(API_ENDPOINTS.LIST_TABLE, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: restaurantId }),
        });
        const json = await res.json();
        if (!res.ok) {
          throw new Error(json?.message || "Failed to fetch tables");
        }
        const list = Array.isArray(json?.list) ? json.list : [];
        return { list };
      },
      { force }
    );
  }, [restaurantId]);

  /* ---- local active detection (ordersitems1{key}) + durations ---- */
  const computeLocalActivity = useCallback(async (rawList) => {
    const list = [...(rawList || [])].map((item) => ({ ...item }));

    for (let i = 0; i < list.length; i++) {
      const key = getTableKey(list[i]) || String(i + 1);
      const order = parseJSON(localStorage.getItem("ordersitems1" + key), null);
      const amount = localStorage.getItem("orderAmount1" + key);
      const start = localStorage.getItem("orderStartTime1" + key);
      const lineItems = parseJSON(
        localStorage.getItem("ordersitems1_lines" + key),
        null
      );

      const hasBase =
        order && typeof order === "object"
          ? Object.keys(order).length > 0
          : !!order;
      const hasLines = Array.isArray(lineItems) && lineItems.length > 0;
      const hasAmount = Number(amount || 0) > 0;

      list[i].hasdata = hasBase || hasLines || hasAmount;
      list[i].amount = safeNumber(amount, 0);
      list[i].itemCount = list[i].hasdata
        ? resolveItemCount({
            lineItems,
            products: order,
            fallback: list[i]?.itemCount,
          })
        : 0;
      list[i].durationSource = start || null;
      list[i].duration = start ? durationFromValue(start) : "0";
    }
    return { list };
  }, []);

  const mergeFirebaseIntoList = useCallback(
    (apiList = [], fb = null, slots = []) => {
      const list = [...(apiList || [])].map((item) => ({ ...item }));
      const firebaseMap = fb && typeof fb === "object" ? { ...fb } : null;

      if (
        firebaseMap &&
        Object.prototype.hasOwnProperty.call(firebaseMap, "undefined")
      ) {
        delete firebaseMap.undefined;
      }

      for (let i = 0; i < list.length; i++) {
        const key = getTableKey(list[i]) || String(i + 1);
        const entry = firebaseMap?.[key];
        if (!entry) {
          list[i].hasdata = false;
          list[i].amount = 0;
          list[i].itemCount = 0;
          list[i].durationSource = null;
          list[i].duration = "0";
          list[i].waitername = null;
          continue;
        }

        const hasdata = buildHasProducts(entry);
        const durationSource = getDurationSourceValue(entry);
        list[i].hasdata = hasdata;
        list[i].amount = safeNumber(entry?.amount, 0);
        list[i].itemCount = hasdata
          ? resolveItemCount({
              lineItems: entry?.line_items,
              products: entry?.products,
              fallback: list[i]?.itemCount,
            })
          : 0;
        list[i].durationSource = hasdata ? durationSource : null;
        list[i].duration = hasdata ? durationFromValue(durationSource) : "0";
        list[i].waitername = entry?.waitername || list[i]?.waitername || null;
      }

      const merged = mergeTakeawaySlots(list, slots);
      const withTakeawayData = merged.list.map((item) => {
        const key = getTableKey(item);
        if (!isTakeawayName(key)) return item;
        const canonical = canonicalizeTakeawayName(key);
        const entry = firebaseMap?.[canonical];
        if (!entry) return item;
        const hasdata = buildHasProducts(entry);
        const durationSource = getDurationSourceValue(entry);
        return {
          ...item,
          name: canonical,
          id: canonical,
          table_key: canonical,
          display_name: formatTakeawayLabel(canonical),
          hasdata,
          amount: safeNumber(entry?.amount, 0),
          itemCount: hasdata
            ? resolveItemCount({
                lineItems: entry?.line_items,
                products: entry?.products,
                fallback: item?.itemCount,
              })
            : 0,
          durationSource: hasdata ? durationSource : null,
          duration: hasdata ? durationFromValue(durationSource) : "0",
          waitername: entry?.waitername || item?.waitername || null,
        };
      });

      return { list: withTakeawayData, slots: merged.slots };
    },
    [mergeTakeawaySlots]
  );

  const applyMergedList = useCallback(
    (activityResult, storedSlots, meta) => {
      const preMerged = activityResult?.list || [];
      const preSlots = activityResult?.slots || storedSlots;

      const merged = activityResult?.slots
        ? { list: preMerged, slots: preSlots }
        : mergeTakeawaySlots(preMerged, storedSlots);

      const mergedWithMeta = mergeMetadata(merged.list, meta);
      persistTakeawaySlots(merged.slots);
      setTableList(mergedWithMeta);
      updateCounts(mergedWithMeta);
    },
    [mergeTakeawaySlots, mergeMetadata, persistTakeawaySlots, updateCounts]
  );

  const applyLiveTableState = useCallback(
    async (firebaseMap = null, opts = {}) => {
      const baseList = opts?.baseList || baseTableListRef.current || [];
      const slots = opts?.slots || takeawaySlotsRef.current || [DEFAULT_TAKEAWAY_NAME];
      const meta = opts?.meta || tableMetaMapRef.current || {};
      const resolvedFirebaseMap =
        firebaseMap && typeof firebaseMap === "object"
          ? firebaseMap
          : firebaseTableStateRef.current &&
            typeof firebaseTableStateRef.current === "object"
          ? firebaseTableStateRef.current
          : null;

      if (!baseList.length) {
        const finalSlots = persistTakeawaySlots(slots);
        const merged = mergeMetadata(
          mergeTakeawaySlots([], finalSlots).list,
          meta
        );
        setTableList(merged);
        updateCounts(merged);
        return;
      }

      if (resolvedFirebaseMap) {
        const activity = mergeFirebaseIntoList(baseList, resolvedFirebaseMap, slots);
        applyMergedList(activity, slots, meta);
        return;
      }

      const localActivity = await computeLocalActivity(baseList);
      applyMergedList(localActivity, slots, meta);
    },
    [
      persistTakeawaySlots,
      mergeMetadata,
      mergeTakeawaySlots,
      updateCounts,
      mergeFirebaseIntoList,
      applyMergedList,
      computeLocalActivity,
    ]
  );

  /* ---- Load flow ---- */
  const load = useCallback(
    async (opts = {}) => {
      loadPlanValidity();

      if (!restaurantId) {
        if (opts?.refresh) setRefreshing(false);
        else setLoading(false);
        setTableList([]);
        setStatusMessage("Missing restaurant information.");
        return;
      }

      const isRefresh = opts?.refresh;
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      try {
        const storedSlots = getStoredTakeawaySlots();
        takeawaySlotsRef.current = storedSlots;
        setTakeawaySlots(storedSlots);

        let baseList = [];
        try {
          const { list: apiList } = await fetchTables({
            force: Boolean(opts?.force),
          });
          baseList = apiList;
          cacheTableList(apiList);
          setStatusMessage("");
        } catch (err) {
          const cached = getCachedTableList();
          if (cached.length > 0) {
            baseList = cached;
            setStatusMessage("Offline mode - showing last saved tables.");
          } else {
            setStatusMessage(err?.message || "Failed to load tables");
            setTableList([]);
            setActiveCount(0);
            setInactiveCount(0);
            return;
          }
        }

        baseTableListRef.current = baseList;
        const meta = buildTableMetaMap(baseList);
        updateTableMetaMap(meta);
        await applyLiveTableState(firebaseTableStateRef.current, {
          baseList,
          slots: storedSlots,
          meta,
        });
      } catch (e) {
        setStatusMessage((prev) => prev || e?.message || "Failed to load tables");
      } finally {
        if (isRefresh) setRefreshing(false);
        else setLoading(false);
      }
    },
    [
      restaurantId,
      loadPlanValidity,
      getStoredTakeawaySlots,
      fetchTables,
      cacheTableList,
      getCachedTableList,
      buildTableMetaMap,
      updateTableMetaMap,
      applyLiveTableState,
    ]
  );

  const handleRefresh = useCallback(() => {
    if (refreshing || loading) return;
    load({ refresh: true, force: true });
  }, [load, refreshing, loading]);

  const rehydrateCurrentTables = useCallback(() => {
    loadPlanValidity();
    applyLiveTableState();
  }, [loadPlanValidity, applyLiveTableState]);

  useEffect(() => {
    const slots = getStoredTakeawaySlots();
    takeawaySlotsRef.current = slots;
    setTakeawaySlots(slots);
  }, [getStoredTakeawaySlots]);

  useEffect(() => {
    setDurationTick((value) => value + 1);
    const intervalId = window.setInterval(() => {
      setDurationTick((value) => value + 1);
    }, 60000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onFocus = () => rehydrateCurrentTables();
    const onCustomRefresh = () => load({ refresh: true, force: true });

    window.addEventListener("focus", onFocus);
    window.addEventListener("tableListRefresh", onCustomRefresh);

    const onVisibility = () => {
      if (document.visibilityState === "visible") onFocus();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("tableListRefresh", onCustomRefresh);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [load, rehydrateCurrentTables]);

  useEffect(() => {
    if (firebaseUnsubscribeRef.current) {
      firebaseUnsubscribeRef.current();
      firebaseUnsubscribeRef.current = null;
    }

    if (!restaurantId) return undefined;

    const db = ensureFirebase();
    const tableRef = ref(db, `/restaurants/${restaurantId}/tables`);
    const unsubscribe = onValue(
      tableRef,
      (snapshot) => {
        const map = snapshot.exists() ? snapshot.val() : null;
        firebaseTableStateRef.current = map && typeof map === "object" ? map : null;
        applyLiveTableState(map);
      },
      () => {
        firebaseTableStateRef.current = null;
        applyLiveTableState(null);
      }
    );

    firebaseUnsubscribeRef.current = unsubscribe;

    return () => {
      if (firebaseUnsubscribeRef.current) {
        firebaseUnsubscribeRef.current();
        firebaseUnsubscribeRef.current = null;
      }
    };
  }, [restaurantId, applyLiveTableState]);

  /* ---- Actions ---- */
  const handleCard = (table, extraState = null) => {
    const tableKey = getTableKey(table) || table?.name;
    if (!tableKey) return;
    // Pass the raw table name as well (matches mobile behaviour where table?.name is used)
    const baseState = {
      tableType: getTableType(table),
      tableLabel: getTableDisplayName(table) || tableKey,
      tableName: table?.name || "",
    };
    navigate(`/order/${encodeURIComponent(tableKey)}`, {
      state: extraState ? { ...baseState, ...extraState } : baseState,
    });
  };

  const handleTakeawayPress = useCallback(() => {
    setTakeawaySlots(getStoredTakeawaySlots());
    setTakeawayManagerOpen(true);
  }, [getStoredTakeawaySlots]);

  const addTakeawaySlot = useCallback(() => {
    const canon = takeawaySlots.map(canonicalizeTakeawayName);
    const highest = canon.reduce(
      (max, s) => Math.max(max, extractTakeawayIndex(s) || 0),
      0
    );
    const next = `TakeAway-${highest + 1}`;
    const nextSlots = persistTakeawaySlots([...canon, next]);

    const merged = mergeTakeawaySlots(tableList, nextSlots);
    const final = mergeMetadata(merged.list);
    setTableList(final);
    updateCounts(final);
  }, [
    takeawaySlots,
    persistTakeawaySlots,
    tableList,
    mergeTakeawaySlots,
    mergeMetadata,
    updateCounts,
  ]);

  const removeTakeawaySlotConfirmed = useCallback(
    async (canonical) => {
      try {
        if (restaurantId) {
          const db = ensureFirebase();
          await set(ref(db, `/restaurants/${restaurantId}/tables/${canonical}`), null);
          await set(
            ref(db, `/restaurants/${restaurantId}/tableswaiters/${canonical}`),
            null
          );
        }
      } catch {}

      const filtered = takeawaySlots.filter(
        (s) => canonicalizeTakeawayName(s) !== canonical
      );
      const nextSlots = persistTakeawaySlots(filtered);
      const filteredList = tableList.filter(
        (t) => canonicalizeTakeawayName(getTableKey(t)) !== canonical
      );

      const merged = mergeTakeawaySlots(filteredList, nextSlots);
      const final = mergeMetadata(merged.list);
      setTableList(final);
      updateCounts(final);
    },
    [
      restaurantId,
      takeawaySlots,
      tableList,
      mergeTakeawaySlots,
      mergeMetadata,
      persistTakeawaySlots,
      updateCounts,
    ]
  );

  const removeTakeawaySlot = async (slotName) => {
    const canonical = canonicalizeTakeawayName(slotName);
    if (takeawaySlots.length <= 1) {
      alert("At least one takeaway slot is required.");
      return;
    }
    const entry = tableList.find(
      (t) => canonicalizeTakeawayName(getTableKey(t)) === canonical
    );
    if (entry?.hasdata) {
      alert("Clear orders from this takeaway before removing it.");
      return;
    }

    if (
      !window.confirm(`Remove ${formatTakeawayLabel(canonical)}? This cannot be undone.`)
    ) {
      return;
    }

    await removeTakeawaySlotConfirmed(canonical);
  };

  const openSlot = (slotName) => {
    setTakeawayManagerOpen(false);
    const canonical = canonicalizeTakeawayName(slotName);
    const target =
      tableList.find((t) => canonicalizeTakeawayName(getTableKey(t)) === canonical) ||
      {
        name: canonical,
        id: canonical,
        table_key: canonical,
        display_name: formatTakeawayLabel(canonical),
    };
    handleCard(target);
  };

  const closeTableActionModal = useCallback(() => {
    setTableActionOpen(false);
    setTableActionLoading(false);
    setTableActionTarget(null);
    setTableActionLines([]);
    setTableActionTotal(0);
    setTableActionMeta({ customerName: "", discountText: "", gstAmount: 0 });
  }, []);

  const clearTableOrderCache = useCallback((tableNo) => {
    if (!tableNo) return;
    localStorage.removeItem("ordersitems1" + tableNo);
    localStorage.removeItem("ordersitems1_lines" + tableNo);
    localStorage.removeItem("orderAmount1" + tableNo);
    localStorage.removeItem("orderStartTime1" + tableNo);
  }, []);

  const clearSingleTableOrder = useCallback(
    async (tableItem) => {
      const tableNo = getTableKey(tableItem);
      if (!tableNo) return;

      const hasActiveOrder = !!tableItem?.hasdata || Number(tableItem?.amount || 0) > 0;
      if (!hasActiveOrder) {
        alert("No active order on this table.");
        return;
      }

      const label = isTakeawayName(tableNo)
        ? formatTakeawayLabel(tableNo)
        : `${getTableType(tableItem) === "room" ? "Room" : "Table"} ${
            getTableDisplayName(tableItem) || tableNo
          }`;

      if (!window.confirm(`Clear order for ${label}?`)) return;

      try {
        clearTableOrderCache(tableNo);

        if (restaurantId) {
          const db = ensureFirebase();
          await set(ref(db, `/restaurants/${restaurantId}/tables/${tableNo}`), null);
          await set(
            ref(db, `/restaurants/${restaurantId}/tableswaiters/${tableNo}`),
            null
          );
        }

        const updated = mergeMetadata(
          tableList.map((item) => {
            const key = getTableKey(item);
            if (String(key) !== String(tableNo)) return item;
            return {
              ...item,
              hasdata: false,
              amount: 0,
              itemCount: 0,
              durationSource: null,
              duration: "0",
              waitername: null,
            };
          })
        );
        setTableList(updated);
        updateCounts(updated);
      } finally {
        closeTableActionModal();
      }
    },
    [
      restaurantId,
      tableList,
      mergeMetadata,
      updateCounts,
      closeTableActionModal,
      clearTableOrderCache,
    ]
  );

  const openTableActionModal = useCallback(
    async (tableItem) => {
      const tableNo = getTableKey(tableItem);
      if (!tableNo) return;

       const cardLooksActive =
        !!tableItem?.hasdata || Number(tableItem?.amount || 0) > 0;

      let remoteTableData = null;
      let waiterMeta = null;

      if (!cardLooksActive && restaurantId) {
        try {
          const db = ensureFirebase();
          const [tableSnap, waiterSnap] = await Promise.all([
            get(ref(db, `/restaurants/${restaurantId}/tables/${tableNo}`)),
            get(ref(db, `/restaurants/${restaurantId}/tableswaiters/${tableNo}`)),
          ]);
          remoteTableData = tableSnap.exists() ? tableSnap.val() : null;
          waiterMeta = waiterSnap.exists() ? waiterSnap.val() : null;
        } catch {}

        const remoteLooksActive =
          buildHasProducts(remoteTableData) ||
          Number(remoteTableData?.amount || 0) > 0;

        if (!remoteLooksActive) {
          clearTableOrderCache(tableNo);
          alert("No active order on this table.");
          return;
        }
      }

      setTableActionTarget(tableItem);
      setTableActionOpen(true);
      setTableActionLoading(true);
      setTableActionLines([]);
      setTableActionTotal(Number(tableItem?.amount || 0));

      try {
        const products = parseJSON(localStorage.getItem("productlistx"), []);
        const partyList = parseJSON(localStorage.getItem("partylistx"), []);
        const storedLines = parseJSON(localStorage.getItem("ordersitems1_lines" + tableNo), null);
        const storedBase = parseJSON(localStorage.getItem("ordersitems1" + tableNo), null);
        const storedAmount = Number(localStorage.getItem("orderAmount1" + tableNo) || 0);

        if (restaurantId && !remoteTableData) {
          try {
            const db = ensureFirebase();
            const [tableSnap, waiterSnap] = await Promise.all([
              get(ref(db, `/restaurants/${restaurantId}/tables/${tableNo}`)),
              get(ref(db, `/restaurants/${restaurantId}/tableswaiters/${tableNo}`)),
            ]);
            remoteTableData = tableSnap.exists() ? tableSnap.val() : null;
            waiterMeta = waiterSnap.exists() ? waiterSnap.val() : null;
          } catch {}
        }

        const productMap = new Map();
        (products || []).forEach((p) => {
          const id = Number(p?.id);
          if (id) productMap.set(id, p);
        });

        let lines = [];
        if (Array.isArray(storedLines) && storedLines.length > 0) {
          lines = storedLines
            .map((line, idx) => {
              const qty = Number(line?.quantity ?? line?.qty ?? 0);
              if (qty <= 0) return null;
              const productId = Number(line?.productid ?? line?.product_id ?? 0);
              const product = productMap.get(productId);
              const name =
                line?.name || product?.name || `Item ${productId || idx + 1}`;
              const unitPrice = Number(
                line?.taxedprice ?? line?.priceaftertax ?? line?.price ?? 0
              );
              return {
                id: line?.lineKey || `${productId || "line"}-${idx}`,
                name,
                quantity: qty,
                unitPrice,
                lineTotal: qty * unitPrice,
              };
            })
            .filter(Boolean);
        } else if (storedBase && typeof storedBase === "object") {
          lines = Object.entries(storedBase)
            .map(([productIdRaw, qtyRaw], idx) => {
              const qty = Number(qtyRaw || 0);
              if (qty <= 0) return null;
              const productId = Number(productIdRaw);
              const product = productMap.get(productId);
              const name = product?.name || `Item ${productId || idx + 1}`;
              const unitPrice = Number(
                product?.taxedprice ?? product?.priceaftertax ?? product?.price ?? 0
              );
              return {
                id: `${productIdRaw}-${idx}`,
                name,
                quantity: qty,
                unitPrice,
                lineTotal: qty * unitPrice,
              };
            })
            .filter(Boolean);
        } else if (remoteTableData?.products) {
          lines = Object.entries(remoteTableData.products)
            .map(([productIdRaw, qtyRaw], idx) => {
              const qty = Number(qtyRaw || 0);
              if (qty <= 0) return null;
              const productId = Number(productIdRaw);
              const product = productMap.get(productId);
              const name = product?.name || `Item ${productId || idx + 1}`;
              const unitPrice = Number(
                product?.taxedprice ?? product?.priceaftertax ?? product?.price ?? 0
              );
              return {
                id: `remote-${productIdRaw}-${idx}`,
                name,
                quantity: qty,
                unitPrice,
                lineTotal: qty * unitPrice,
              };
            })
            .filter(Boolean);
        }

        const fallbackTotal = lines.reduce(
          (sum, line) => sum + Number(line?.lineTotal || 0),
          0
        );
        const resolvedTotal = Number(
          storedAmount || remoteTableData?.amount || tableItem?.amount || 0
        );

        let customerName = "";
        let discountText = "";
        if (waiterMeta) {
          const selectedPartyId = Number(waiterMeta?.selectedpartyid || 0);
          if (selectedPartyId && Array.isArray(partyList)) {
            const match = partyList.find(
              (party) => Number(party?.id) === selectedPartyId
            );
            customerName = String(match?.partyname || "").trim();
          }
          const discountMode = waiterMeta?.discountMode;
          const discountAmount = Number(waiterMeta?.discountAmount || 0);
          const discountPercent = Number(waiterMeta?.discountPercentage || 0);
          if (discountMode === "amount" && discountAmount > 0) {
            discountText = fmtMoney(discountAmount);
          } else if (discountPercent > 0) {
            discountText = `${discountPercent}%`;
          }
        }

        const gstAmount = (Array.isArray(storedLines) ? storedLines : []).reduce(
          (sum, line) => {
            const qty = Number(line?.quantity ?? line?.qty ?? 0);
            const tax = Number(line?.taxamount ?? 0);
            return sum + qty * tax;
          },
          0
        );

        setTableActionLines(lines);
        setTableActionTotal(resolvedTotal > 0 ? resolvedTotal : fallbackTotal);
        setTableActionMeta({ customerName, discountText, gstAmount });
      } catch {
        setTableActionLines([]);
        setTableActionTotal(Number(tableItem?.amount || 0));
        setTableActionMeta({ customerName: "", discountText: "", gstAmount: 0 });
      } finally {
        setTableActionLoading(false);
      }
    },
    [restaurantId, clearTableOrderCache]
  );

  const clearAllTables = async () => {
    if (
      !window.confirm(
        "This will clear all running tables and takeaway orders. Continue?"
      )
    ) {
      return;
    }
    try {
      setClearing(true);

      const next = [...tableList].map((t) => ({
        ...t,
        hasdata: false,
        amount: 0,
        itemCount: 0,
        durationSource: null,
        duration: "0",
        waitername: null,
      }));

      for (const t of next) {
        const tableNo = getTableKey(t);
        if (!tableNo) continue;
        clearTableOrderCache(tableNo);
      }

      try {
        if (restaurantId) {
          const db = ensureFirebase();
          for (const t of next) {
            const key = getTableKey(t);
            if (!key) continue;
            await set(ref(db, `/restaurants/${restaurantId}/tables/${key}`), null);
            await set(
              ref(db, `/restaurants/${restaurantId}/tableswaiters/${key}`),
              null
            );
          }
        }
      } catch {}

      setTableList(next);
      updateCounts(next);
      alert("All tables cleared.");
    } finally {
      setClearing(false);
    }
  };

  useEffect(() => {
    const onShortcut = (event) => {
      const id = event?.detail?.id;
      if (!String(id || "").startsWith("tables.")) return;

      const hasBlockingModal = tableActionOpen || takeawayManagerOpen;

      switch (id) {
        case "tables.refresh":
          if (hasBlockingModal) return;
          event.preventDefault();
          handleRefresh();
          return;
        case "tables.takeaway-manager":
          if (tableActionOpen) return;
          event.preventDefault();
          handleTakeawayPress();
          return;
        case "tables.add-takeaway-slot":
          if (tableActionOpen) return;
          event.preventDefault();
          setTakeawayManagerOpen(true);
          addTakeawaySlot();
          return;
        case "tables.add-table":
          if (hasBlockingModal) return;
          event.preventDefault();
          navigate("/add-table");
          return;
        default:
          return;
      }
    };

    window.addEventListener(APP_SHORTCUT_EVENT, onShortcut);
    return () => window.removeEventListener(APP_SHORTCUT_EVENT, onShortcut);
  }, [
    addTakeawaySlot,
    handleRefresh,
    handleTakeawayPress,
    navigate,
    tableActionOpen,
    takeawayManagerOpen,
  ]);

  useEffect(() => {
    const onEscape = (event) => {
      if (tableActionOpen) {
        closeTableActionModal();
        event.preventDefault();
        return;
      }
      if (takeawayManagerOpen) {
        setTakeawayManagerOpen(false);
        event.preventDefault();
      }
    };

    window.addEventListener(APP_ESCAPE_EVENT, onEscape);
    return () => window.removeEventListener(APP_ESCAPE_EVENT, onEscape);
  }, [closeTableActionModal, tableActionOpen, takeawayManagerOpen]);

  const roomTableList = tableList.filter((item) => getTableType(item) === "room");
  const regularTableList = tableList.filter(
    (item) => getTableType(item) !== "room"
  );
  const takeawayCount = takeawaySlots.length;
  const roomCount = roomTableList.length;
  const occupancyPct = tableList.length
    ? Math.round((activeCount / tableList.length) * 100)
    : 0;

  const getDisplayDuration = (item) => {
    const durationSource = getDurationSourceValue(item);
    if (item?.hasdata && durationSource) {
      return durationFromValue(durationSource);
    }
    return item?.duration || "0";
  };

  const renderTableCard = (t, idx) => {
    const tableKey = getTableKey(t);
    const isActive = !!t?.hasdata;
    const isTA = isTakeawayName(tableKey);
    const tableType = getTableType(t);
    const baseLabel = isTA
      ? formatTakeawayLabel(tableKey)
      : getTableDisplayName(t) || tableKey;
    const label = isTA
      ? baseLabel
      : `${tableType === "room" ? "Room" : "Table"} ${baseLabel}`;
    const durationText = getDisplayDuration(t);
    const itemCount = normalizeItemCount(t?.itemCount);
    const itemCountLabel = itemCount > 99 ? "99+" : String(itemCount);
    const circlePrefix = isTA ? "TA" : tableType === "room" ? "Room" : "Table";
    const circleValue = isTA
      ? baseLabel.replace(/^TakeAway\s*/i, "").trim() || "1"
      : String(baseLabel || "").trim() || "-";
    const note = typeof t?.notes === "string" ? t.notes.trim() : null;
    const displayNote = note && note.length > 120 ? `${note.slice(0, 117)}...` : note;
    const seatValue =
      t?.seatcapacity === 0 || t?.seatcapacity === "0"
        ? 0
        : t?.seatcapacity
        ? Number(t.seatcapacity)
        : null;

    return (
      <div
        key={tableKey || t?.id || idx}
        className="table-card"
        style={{
          ...styles.card,
          ...(isActive ? styles.cardActive : {}),
        }}
        onClick={() => handleCard(t)}
        onContextMenu={(e) => {
          e.preventDefault();
          openTableActionModal(t);
        }}
        onMouseDown={() => {
          pressTimeoutRef.current = setTimeout(() => {
            openTableActionModal(t);
          }, 520);
        }}
        onMouseUp={() => {
          clearTimeout(pressTimeoutRef.current);
          pressTimeoutRef.current = null;
        }}
        onMouseLeave={() => {
          clearTimeout(pressTimeoutRef.current);
          pressTimeoutRef.current = null;
        }}
      >
        <div
          style={{
            ...styles.cardGlow,
            ...(isActive ? styles.cardGlowActive : styles.cardGlowIdle),
          }}
        />
        {isActive && itemCount > 0 ? (
          <div style={styles.itemCountBadge}>{itemCountLabel}</div>
        ) : null}
        <div
          style={{
            ...styles.cardTopRow,
            paddingLeft: isActive && itemCount > 0 ? 42 : 0,
          }}
        >
          <div style={styles.amountPill}>{fmtMoney(t?.amount)}</div>
          <div
            style={{
              ...styles.statusPill,
              ...(isActive ? styles.statusPillActive : styles.statusPillIdle),
            }}
          >
            {isActive ? "Running" : "Ready"}
          </div>
        </div>

        <div style={styles.circleWrap}>
          <div style={{ ...styles.circle, ...(isActive ? {} : styles.circleMuted) }}>
            <span
              style={{
                ...styles.circlePrefix,
                ...(isActive ? {} : { color: brandColor }),
              }}
            >
              {circlePrefix}
            </span>
            <span
              style={{
                ...styles.circleText,
                fontSize: getCircleLabelFontSize(circleValue),
                ...(isActive ? {} : { color: brandColor }),
              }}
              title={label}
            >
              {circleValue}
            </span>
          </div>
        </div>

        <div style={styles.cardLabel}>{label}</div>

        <div style={styles.meta}>
          <div style={styles.metaRow}>
            <span style={styles.metaKey}>{isTA ? "Slot" : "Waiter"}</span>
            <span style={styles.metaVal}>{isTA ? baseLabel : t?.waitername || "Not Assigned"}</span>
          </div>
          <div style={styles.metaRow}>
            <span style={styles.metaKey}>Time</span>
            <span style={styles.metaVal}>
              <FaClock style={styles.inlineIcon} />
              {durationText}
            </span>
          </div>
          {!isTA && seatValue !== null && Number.isFinite(seatValue) ? (
            <div style={styles.metaRow}>
              <span style={styles.metaKey}>Seats</span>
              <span style={styles.metaVal}>{seatValue}</span>
            </div>
          ) : null}
          {tableType === "room" ? (
            <div style={styles.metaRow}>
              <span style={styles.roomBadge}>Room</span>
            </div>
          ) : null}
          {displayNote ? (
            <div style={{ ...styles.metaRow, alignItems: "start" }}>
              <span style={styles.metaKey}>Notes</span>
              <span style={{ ...styles.metaVal, whiteSpace: "pre-wrap" }}>{displayNote}</span>
            </div>
          ) : null}
        </div>

        <div style={styles.cardFooter}>
          <span style={styles.cardFooterText}>
            {isActive ? "Tap to continue billing" : "Tap to start new order"}
          </span>
          <span style={styles.cardFooterHint}>Hold for actions</span>
        </div>
      </div>
    );
  };

  /* ---- UI ---- */
  return (
    <div style={styles.container}>
      {/* Header */}
      <div className="table-page-responsive-hero" style={styles.heroPanel}>
        <div style={styles.heroCopy}>
          <div style={styles.kicker}>Dining Floor</div>
          <h2 style={styles.title}>Table Control Center</h2>
          <div style={styles.subtitle}>
            Live occupancy, running bills, takeaway slots, and quick actions in one place.
          </div>
          <div style={styles.heroMetaRow}>
            <Chip tone="success">{occupancyPct}% Occupied</Chip>
            <Chip tone="muted">{tableList.length} Total Spaces</Chip>
          </div>
        </div>

        <div style={styles.headerCard}>
          <div className="table-page-responsive-actions" style={styles.headerActions}>
            <button
              style={styles.ghostBtn}
              onClick={handleRefresh}
            >
              <span style={styles.btnContent}>
                <FaSyncAlt />
                Refresh
              </span>
            </button>
            <button
              style={styles.ghostBtn}
              onClick={handleTakeawayPress}
              onContextMenu={(e) => {
                e.preventDefault();
                addTakeawaySlot();
              }}
              title="Right click to quick add takeaway slot"
            >
              <span style={styles.btnContent}>
                <FaShoppingBag />
                Takeaway
                {takeawaySlots.length > 1 ? ` (${takeawaySlots.length})` : ""}
              </span>
            </button>
            <button style={styles.greenBtn} onClick={() => navigate("/add-table")}>
              <span style={styles.btnContent}>
                <FaPlus />
                Add Table
              </span>
            </button>
            <button
              style={{ ...styles.dangerBtn, opacity: clearing ? 0.7 : 1 }}
              onClick={clearAllTables}
              disabled={clearing}
            >
              {clearing ? "Clearing..." : "Clear All"}
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="table-page-responsive-stats" style={styles.statsGrid}>
        <div style={{ ...styles.statCard, ...styles.statCardActive }}>
          <div style={styles.statLabel}>Active Tables</div>
          <div style={styles.statValue}>{activeCount}</div>
          <div style={styles.statHint}>Orders currently running</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Available</div>
          <div style={styles.statValue}>{inactiveCount}</div>
          <div style={styles.statHint}>Ready for fresh orders</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Takeaway Slots</div>
          <div style={styles.statValue}>{takeawayCount}</div>
          <div style={styles.statHint}>Fast parcel order lanes</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Rooms</div>
          <div style={styles.statValue}>{roomCount}</div>
          <div style={styles.statHint}>Private dining spaces</div>
        </div>
      </div>

      {statusMessage ? <div style={styles.statusBanner}>{statusMessage}</div> : null}

      {planExpired ? (
        <div style={styles.expiredOverlay}>
          <div style={styles.expiredCard}>
            <h3 style={styles.expiredTitle}>Plan expired</h3>
            <p style={styles.expiredSub}>Your plan has expired. Renew to continue full table operations.</p>
            <button style={styles.expiredBtn} onClick={() => navigate("/plan-selection")}>Update Plan</button>
          </div>
        </div>
      ) : null}

      {/* Content */}
      {loading && tableList.length === 0 ? (
        <p style={styles.statusText}>Loading...</p>
      ) : regularTableList.length === 0 && roomTableList.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyTitle}>No tables found</div>
          <div style={styles.emptySub}>
            Add your first table to start managing dine-in and takeaway orders.
          </div>
          <button style={styles.greenBtn} onClick={() => navigate("/add-table")}>
            <span style={styles.btnContent}>
              <FaPlus />
              Add First Table
            </span>
          </button>
        </div>
      ) : (
        <>
          {refreshing ? <p style={{ ...styles.statusText, marginTop: 0 }}>Refreshing...</p> : null}

          {regularTableList.length > 0 ? (
            <div style={styles.sectionCard}>
              <div style={styles.sectionHead}>
                <div>
                  <div style={styles.sectionEyebrow}>Dining Tables</div>
                  <h4 style={styles.sectionTitle}>Floor Tables</h4>
                </div>
                <div style={styles.sectionCount}>{regularTableList.length}</div>
              </div>
              <div className="table-page-responsive-grid" style={styles.grid}>{regularTableList.map(renderTableCard)}</div>
            </div>
          ) : null}

          {roomTableList.length > 0 ? (
            <div style={{ ...styles.sectionCard, ...styles.roomSection }}>
              <div style={styles.sectionHead}>
                <div>
                  <div style={styles.sectionEyebrow}>Private Spaces</div>
                  <h4 style={styles.sectionTitle}>Rooms</h4>
                </div>
                <div style={styles.sectionCount}>{roomTableList.length}</div>
              </div>
              <div className="table-page-responsive-grid" style={styles.grid}>{roomTableList.map(renderTableCard)}</div>
            </div>
          ) : null}
        </>
      )}

      {/* Takeaway Manager */}
      <Modal
        open={takeawayManagerOpen}
        onClose={() => setTakeawayManagerOpen(false)}
        title="Takeaway Slots"
      >
        <p style={{ color: "#64748B", marginTop: 0 }}>
          Tap a slot to open it. Remove only when no orders are running.
        </p>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            marginTop: 10,
          }}
        >
          {takeawaySlots.map((slot) => {
            const canonical = canonicalizeTakeawayName(slot);
            const entry = tableList.find(
              (t) => canonicalizeTakeawayName(getTableKey(t)) === canonical
            );
            const isActive = !!entry?.hasdata;
            const amount = Number(entry?.amount || 0).toFixed(2);

            return (
              <div
                key={canonical}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border: "1px solid #E2E8F0",
                  background: "#F8FAFC",
                  borderRadius: 14,
                  padding: "12px 14px",
                }}
              >
                <button
                  style={{
                    background: "transparent",
                    border: "none",
                    textAlign: "left",
                    cursor: "pointer",
                    color: "#0F172A",
                    fontWeight: 800,
                  }}
                  onClick={() => openSlot(canonical)}
                >
                  {formatTakeawayLabel(canonical)}
                  <div style={{ fontWeight: 500, color: "#475569", fontSize: 12 }}>
                    {isActive ? `Active • ${fmtMoney(amount)}` : "Idle"}
                  </div>
                </button>

                <button
                  onClick={() => removeTakeawaySlot(canonical)}
                  disabled={isActive || takeawaySlots.length <= 1}
                  title={
                    isActive || takeawaySlots.length <= 1
                      ? "Cannot remove"
                      : "Remove"
                  }
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 18,
                    border: "none",
                    cursor:
                      isActive || takeawaySlots.length <= 1
                        ? "not-allowed"
                        : "pointer",
                    background:
                      isActive || takeawaySlots.length <= 1
                        ? "#F1F5F9"
                        : "#FEE2E2",
                    color:
                      isActive || takeawaySlots.length <= 1
                        ? "#CBD5E1"
                        : "#B91C1C",
                    fontWeight: 900,
                  }}
                >
                  <FaTrashAlt />
                </button>
              </div>
            );
          })}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
          <button onClick={addTakeawaySlot} style={styles.primaryBtn}>
            <span style={styles.btnContent}>
              <FaPlus />
              Add Takeaway Slot
            </span>
          </button>
        </div>
      </Modal>

      {/* Table Actions */}
      <Modal
        open={tableActionOpen}
        onClose={closeTableActionModal}
        title={`Table Actions${tableActionTarget ? ` - ${getTableDisplayName(tableActionTarget) || getTableKey(tableActionTarget)}` : ""}`}
      >
        {tableActionLoading ? (
          <p style={styles.statusText}>Loading order summary...</p>
        ) : (
          <>
            <div style={styles.actionHeadRow}>
              <span style={{ ...styles.actionCellItem, fontWeight: 800 }}>Item</span>
              <span style={styles.actionCellValue}>Qty</span>
              <span style={styles.actionCellValue}>Rate</span>
              <span style={styles.actionCellValue}>Total</span>
            </div>
            <div style={styles.actionListWrap}>
              {tableActionLines.length === 0 ? (
                <p style={styles.statusText}>No order items found.</p>
              ) : (
                tableActionLines.map((line) => (
                  <div key={line.id} style={styles.actionRow}>
                    <span style={styles.actionCellItem}>{line.name}</span>
                    <span style={styles.actionCellValue}>{line.quantity}</span>
                    <span style={styles.actionCellValue}>{fmtMoney(line.unitPrice)}</span>
                    <span style={styles.actionCellValue}>{fmtMoney(line.lineTotal)}</span>
                  </div>
                ))
              )}
            </div>

            <div style={styles.totalRow}>
              <strong>Total</strong>
              <strong>{fmtMoney(tableActionTotal)}</strong>
            </div>

            {tableActionMeta?.customerName ||
            tableActionMeta?.discountText ||
            Number(tableActionMeta?.gstAmount || 0) > 0 ? (
              <div style={styles.metaCard}>
                {tableActionMeta?.customerName ? (
                  <div style={styles.metaInfoRow}>
                    <span>Customer</span>
                    <strong>{tableActionMeta.customerName}</strong>
                  </div>
                ) : null}
                {tableActionMeta?.discountText ? (
                  <div style={styles.metaInfoRow}>
                    <span>Discount</span>
                    <strong>{tableActionMeta.discountText}</strong>
                  </div>
                ) : null}
                {Number(tableActionMeta?.gstAmount || 0) > 0 ? (
                  <div style={styles.metaInfoRow}>
                    <span>{taxDisplayLabel}</span>
                    <strong>{fmtMoney(tableActionMeta.gstAmount)}</strong>
                  </div>
                ) : null}
              </div>
            ) : null}

            <div style={styles.actionBtnRow}>
              <button
                style={styles.primaryBtn}
                onClick={() =>
                  handleCard(tableActionTarget, {
                    autoAction: "quick_save",
                    autoReturn: true,
                    autoActionNonce: `${Date.now()}-${getTableKey(tableActionTarget)}`,
                  })
                }
              >
                Quick Save
              </button>
              <button
                style={{ ...styles.primaryBtn, backgroundColor: "#0EA5E9" }}
                onClick={() =>
                  handleCard(tableActionTarget, {
                    autoAction: "quick_print",
                    autoReturn: true,
                    autoActionNonce: `${Date.now()}-${getTableKey(tableActionTarget)}`,
                  })
                }
              >
                Quick Print
              </button>
            </div>
            <div style={styles.actionBtnRow}>
              <button
                style={{ ...styles.primaryBtn, backgroundColor: "#64748B" }}
                onClick={() => handleCard(tableActionTarget)}
              >
                View Bill
              </button>
              <button
                style={styles.dangerBtn}
                onClick={() => clearSingleTableOrder(tableActionTarget)}
              >
                Clear Table
              </button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default TablePage;

/* ---------------- Styles ---------------- */
const styles = {
  container: {
    padding: 18,
    fontFamily:
      '"Sora","Plus Jakarta Sans","Segoe UI",Roboto,Arial,sans-serif',
    background: "#edf2f7",
    minHeight: "100vh",
    position: "relative",
  },
  heroPanel: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.5fr) minmax(360px, 1fr)",
    gap: 10,
    alignItems: "start",
    marginBottom: 10,
  },
  heroCopy: {
    padding: "12px 16px",
    borderRadius: 12,
    background: `linear-gradient(135deg, ${brandColor} 0%, #014a7f 100%)`,
    color: "#ffffff",
    boxShadow: "0 6px 18px rgba(1,91,154,.12)",
    border: "1px solid rgba(255,255,255,.1)",
  },
  headerCard: {
    display: "flex",
    justifyContent: "center",
    gap: 12,
    alignItems: "center",
    flexWrap: "wrap",
    padding: "10px",
    borderRadius: 12,
    background: "#ffffff",
    border: "1px solid #d7dee8",
    boxShadow: "0 4px 14px rgba(15,23,42,.05)",
  },
  headerActions: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
    width: "100%",
  },
  kicker: {
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: 1.2,
    color: "#94a3b8",
    fontWeight: 800,
  },
  title: { margin: "2px 0 0", fontSize: 17, fontWeight: 900, color: "#f8fafc" },
  subtitle: {
    marginTop: 5,
    color: "#cbd5e1",
    fontSize: 11,
    fontWeight: 500,
    maxWidth: 520,
    lineHeight: 1.35,
  },
  heroMetaRow: {
    marginTop: 8,
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
  },
  greenBtn: {
    padding: "9px 12px",
    background: brandColor,
    color: "#fff",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    fontWeight: 800,
    boxShadow: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dangerBtn: {
    padding: "9px 12px",
    background: "#dc2626",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    fontWeight: 800,
    boxShadow: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  ghostBtn: {
    padding: "9px 12px",
    backgroundColor: "#f8fafc",
    color: "#0f172a",
    border: "1px solid #d7dee8",
    borderRadius: 10,
    cursor: "pointer",
    fontWeight: 800,
    boxShadow: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  primaryBtn: {
    padding: "8px 13px",
    background: brandColor,
    color: "#fff",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    fontWeight: 800,
    boxShadow: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  btnContent: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 8,
    marginBottom: 10,
  },
  statCard: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid #d7dee8",
    background: "#ffffff",
    boxShadow: "none",
  },
  statCardActive: {
    background: brandColor,
    color: "#fff",
    border: `1px solid ${brandColor}`,
  },
  statLabel: {
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: 1.1,
    fontWeight: 800,
    opacity: 0.8,
  },
  statValue: {
    marginTop: 4,
    fontSize: 18,
    lineHeight: 1,
    fontWeight: 900,
    color: "inherit",
  },
  statHint: {
    marginTop: 4,
    fontSize: 10.5,
    lineHeight: 1.3,
    color: "inherit",
    opacity: 0.72,
  },
  statusBanner: {
    padding: "9px 12px",
    backgroundColor: "#f8fafc",
    border: "1px solid #d7dee8",
    borderRadius: 10,
    color: "#0f172a",
    fontWeight: 700,
    marginBottom: 10,
    boxShadow: "none",
  },
  statusText: { color: "#6b7280", fontStyle: "italic" },
  sectionCard: {
    marginTop: 6,
    padding: 14,
    borderRadius: 16,
    border: "1px solid #d7dee8",
    background: "#ffffff",
    boxShadow: "none",
  },
  sectionHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 12,
  },
  sectionEyebrow: {
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 1.4,
    color: "#64748b",
  },
  sectionTitle: {
    margin: "2px 0 0",
    color: "#0f172a",
    fontSize: 17,
    fontWeight: 900,
  },
  sectionCount: {
    minWidth: 34,
    height: 34,
    borderRadius: 17,
    display: "grid",
    placeItems: "center",
    background: "#f1f5f9",
    color: brandColor,
    fontWeight: 900,
    fontSize: 13,
    border: "1px solid #bfdbfe",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
    justifyContent: "stretch",
    gap: 12,
  },
  roomSection: { marginTop: 18 },
  card: {
    position: "relative",
    backgroundColor: "#ffffff",
    borderRadius: 16,
    padding: 13,
    border: "1px solid #d7dee8",
    boxShadow: "none",
    cursor: "pointer",
    transition: "transform .18s ease, box-shadow .18s ease",
    overflow: "hidden",
  },
  cardActive: {
    background: "#eaf4ff",
    border: "1px solid #5ea6db",
  },
  cardGlow: {
    position: "absolute",
    left: -34,
    top: -34,
    width: 84,
    height: 84,
    borderRadius: "50%",
    filter: "blur(4px)",
    opacity: 0.95,
    pointerEvents: "none",
  },
  cardGlowActive: {
    background:
      "radial-gradient(circle, rgba(1,91,154,.14) 0%, rgba(1,91,154,.03) 70%, transparent 100%)",
  },
  cardGlowIdle: {
    background:
      "radial-gradient(circle, rgba(100,116,139,.10) 0%, rgba(100,116,139,.02) 70%, transparent 100%)",
  },
  cardTopRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
    marginBottom: 10,
  },
  amountPill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "7px 10px",
    borderRadius: 999,
    background: "#f8fbff",
    border: "1px solid #cddff1",
    color: "#11324d",
    fontSize: 13,
    fontWeight: 900,
    letterSpacing: -0.2,
    minHeight: 30,
  },
  statusPill: {
    padding: "6px 8px",
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 0.8,
  },
  statusPillActive: {
    background: "#0b6fb5",
    color: "#ffffff",
    border: "1px solid #0b6fb5",
  },
  statusPillIdle: {
    background: "#e2e8f0",
    color: "#334155",
    border: "1px solid #cbd5e1",
  },
  circleWrap: { display: "flex", justifyContent: "center", marginBottom: 10 },
  circle: {
    width: 68,
    minWidth: 68,
    height: 68,
    borderRadius: 20,
    border: `1px solid rgba(1,91,154,.16)`,
    background: `linear-gradient(180deg, ${brandColor}, #014a7f)`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "6px 5px",
    textAlign: "center",
    gap: 2,
    overflow: "hidden",
    boxShadow: "none",
  },
  circleMuted: {
    background: "#ffffff",
    boxShadow: "none",
  },
  circlePrefix: {
    color: "#fff",
    fontWeight: 800,
    fontSize: 10,
    lineHeight: 1,
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  circleText: {
    color: "#fff",
    fontWeight: 900,
    fontSize: 13,
    lineHeight: 1.05,
    maxWidth: "100%",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },
  cardLabel: {
    marginBottom: 10,
    textAlign: "center",
    color: "#0f172a",
    fontWeight: 900,
    fontSize: 13,
    lineHeight: 1.35,
    minHeight: 34,
  },
  meta: {
    marginTop: 2,
    display: "grid",
    gap: 6,
    padding: "10px",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
  },
  metaRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
    fontSize: 11,
    color: "#374151",
  },
  metaKey: {
    color: "#64748b",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    fontSize: 10,
  },
  metaVal: {
    color: "#111827",
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    textAlign: "right",
  },
  inlineIcon: { color: "#64748b", fontSize: 11 },
  roomBadge: {
    fontSize: 11,
    fontWeight: 700,
    borderRadius: 999,
    padding: "2px 8px",
    background: "#e2e8f0",
    color: "#334155",
  },
  itemCountBadge: {
    position: "absolute",
    top: 12,
    left: 12,
    minWidth: 28,
    height: 28,
    padding: "0 8px",
    borderRadius: 999,
    background: "#014a7f",
    color: "#ffffff",
    border: "1px solid rgba(255,255,255,.22)",
    display: "grid",
    placeItems: "center",
    fontSize: 12,
    fontWeight: 900,
    boxShadow: "0 8px 14px rgba(1,91,154,.16)",
  },
  cardFooter: {
    marginTop: 10,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
    fontSize: 10,
    paddingTop: 8,
    borderTop: "1px dashed #dbe5ef",
  },
  cardFooterText: {
    color: "#334155",
    fontWeight: 700,
  },
  cardFooterHint: {
    color: "#64748b",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  expiredOverlay: {
    position: "absolute",
    inset: 0,
    backgroundColor: "rgba(255,255,255,0.7)",
    display: "grid",
    placeItems: "center",
    padding: 20,
    zIndex: 20,
  },
  expiredCard: {
    width: "100%",
    maxWidth: 360,
    background: "rgba(255,255,255,0.92)",
    borderRadius: 20,
    padding: 20,
    textAlign: "center",
    border: "1px solid #E2E8F0",
  },
  expiredTitle: { margin: "0 0 8px", color: "#0F172A" },
  expiredSub: { margin: "0 0 12px", color: "#475569", fontSize: 13 },
  expiredBtn: {
    border: "none",
    borderRadius: 999,
    background: "#FBBF24",
    color: "#0F172A",
    padding: "10px 16px",
    fontWeight: 800,
    cursor: "pointer",
  },
  emptyState: {
    padding: "28px 18px",
    borderRadius: 18,
    border: "1px dashed #cbd5e1",
    background: "#ffffff",
    display: "grid",
    justifyItems: "center",
    gap: 12,
    textAlign: "center",
  },
  emptyTitle: {
    color: "#0f172a",
    fontWeight: 900,
    fontSize: 20,
  },
  emptySub: {
    maxWidth: 460,
    color: "#64748b",
    lineHeight: 1.55,
    fontSize: 13,
  },
  actionHeadRow: {
    display: "grid",
    gridTemplateColumns: "2fr 1fr 1fr 1fr",
    gap: 8,
    borderBottom: "1px solid #E2E8F0",
    paddingBottom: 6,
    marginBottom: 8,
    fontSize: 12,
    color: "#334155",
  },
  actionListWrap: { maxHeight: 240, overflowY: "auto" },
  actionRow: {
    display: "grid",
    gridTemplateColumns: "2fr 1fr 1fr 1fr",
    gap: 8,
    padding: "6px 0",
    borderBottom: "1px dashed #E2E8F0",
    fontSize: 12,
  },
  actionCellItem: { color: "#0F172A" },
  actionCellValue: { textAlign: "right", color: "#334155" },
  totalRow: {
    marginTop: 10,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    color: "#0F172A",
  },
  metaCard: {
    marginTop: 10,
    border: "1px solid #E2E8F0",
    borderRadius: 10,
    padding: 10,
    background: "#F8FAFC",
  },
  metaInfoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    color: "#334155",
    padding: "2px 0",
  },
  actionBtnRow: {
    marginTop: 10,
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 8,
  },
};

/* ---------------- Hover polish ---------------- */
if (
  typeof document !== "undefined" &&
  !document.getElementById("tablepage-wow-style")
) {
  const styleTag = document.createElement("style");
  styleTag.id = "tablepage-wow-style";
  styleTag.innerHTML = `
    .table-card:hover{
      transform: translateY(-2px);
      box-shadow: 0 14px 26px rgba(15,23,42,.10);
    }
    .table-card:active{
      transform: translateY(-1px) scale(.99);
    }
    @media (max-width: 1120px){
      .table-page-responsive-hero{
        grid-template-columns: 1fr !important;
      }
      .table-page-responsive-stats{
        grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      }
    }
    @media (max-width: 720px){
      .table-page-responsive-actions{
        grid-template-columns: 1fr !important;
      }
      .table-page-responsive-stats{
        grid-template-columns: 1fr !important;
      }
      .table-page-responsive-grid{
        grid-template-columns: 1fr !important;
      }
    }
  `;
  document.head.appendChild(styleTag);
}
